import type { TopupMethod, TopupRequestStatus } from "@/modules/wallet/domain/types";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { DatePicker } from "@/shared/ui/DatePicker";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Input } from "@/components/ui/input";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { TOPUP_STATUS_LABELS } from "./topup-queue-labels";

const FILTER_ALL = "__all__" as const;

type TopupQueueSearch = {
  requestedFrom?: string;
  requestedTo?: string;
  search?: string;
  status?: TopupRequestStatus;
  method?: TopupMethod;
  offset?: number;
  limit?: number;
};

const STATUS_OPTIONS = [
  { value: FILTER_ALL, label: "ທຸກສະຖານະ" },
  ...(
    ["PENDING", "APPROVED", "REJECTED"] as const
  ).map((s) => ({ value: s, label: TOPUP_STATUS_LABELS[s] })),
];

const METHOD_OPTIONS = [
  { value: FILTER_ALL, label: "ທຸກວິທີ" },
  { value: "BANK_SLIP", label: "ໂອນ + ສລິບ" },
  { value: "CASHIER", label: "ເຄົ້າເຕີ (POS QR)" },
];

export function TopupQueueFilter() {
  const nav = useNavigate({ from: "/app/wallet/topup-queue" });
  const search = useSearch({
    from: "/app/wallet/topup-queue",
  }) as TopupQueueSearch;

  const hasFilter =
    !!search.requestedFrom ||
    !!search.requestedTo ||
    !!search.search ||
    !!search.status ||
    !!search.method;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );
  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchInputValue(val);
    update({ search: val || undefined });
  }, 400);

  const update = (next: Partial<TopupQueueSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <SimpleSelect
        placeholder="ສະຖານະ"
        value={search.status ?? FILTER_ALL}
        onValueChange={(v) =>
          update({
            status:
              v === FILTER_ALL || !v
                ? undefined
                : (v as TopupRequestStatus),
          })
        }
        options={STATUS_OPTIONS}
        className="h-9 w-40"
      />
      <SimpleSelect
        placeholder="ວິທີ"
        value={search.method ?? FILTER_ALL}
        onValueChange={(v) =>
          update({
            method:
              v === FILTER_ALL || !v ? undefined : (v as TopupMethod),
          })
        }
        options={METHOD_OPTIONS}
        className="h-9 w-44"
      />
      <DatePicker
        mode="range"
        value={{
          from: search.requestedFrom
            ? parseISO(search.requestedFrom)
            : undefined,
          to: search.requestedTo ? parseISO(search.requestedTo) : undefined,
        }}
        onChange={(range) => {
          const from = Array.isArray(range)
            ? range[0]
            : (range as { from?: Date; to?: Date })?.from;
          const to = Array.isArray(range)
            ? range[1]
            : (range as { from?: Date; to?: Date })?.to;
          update({
            requestedFrom: from ? formatDateForInput(from) : undefined,
            requestedTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ວັນທີຮ້ອງຂໍ"
        className="h-9 w-fit min-w-56"
      />
      <Input
        type="search"
        placeholder="ຄົ້ນຫາລູກຄ້າ (ຊື່ / ເບີ)"
        className="w-56"
        defaultValue={searchInputValue}
        onChange={(e) => {
          debouncedSearch(e.target.value);
        }}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
